import type { TermsToAccept } from '@funkit/connect-core'
import type { ReactNode } from 'react'
import { Text as InlineText, Linking, StyleSheet } from 'react-native'

import { withAlpha } from '../utils/withAlpha'
import { logger } from '../logging/logger'
import { FunkitTrans } from '../providers/FunkitTrans'
import { Text, useTheme } from '../theme/restyle'

export interface FiatTermsNoticeProps {
  /** A standing disclosure names only its document; there is no revision to echo back. */
  terms: TermsToAccept | Pick<TermsToAccept, 'documentUrl'>
}

export const COINBASE_USER_AGREEMENT_URL =
  'https://www.coinbase.com/legal/user_agreement'
const COINBASE_PRIVACY_POLICY_URL = 'https://www.coinbase.com/legal/privacy'

function openDocument(url: string) {
  void Linking.openURL(url).catch(() => {
    logger.warn('connect-rn:fiat:terms_document_not_opened')
  })
}

/** A tagged span of the notice; the label is the tag's translated content. */
function DocumentLink({
  url,
  children,
}: {
  url: string
  children?: ReactNode
}) {
  const theme = useTheme()
  return (
    <InlineText
      style={[
        styles.link,
        { color: withAlpha(theme.content.elements.primary, 0.5) },
      ]}
      accessibilityRole="link"
      onPress={() => openDocument(url)}
    >
      {children}
    </InlineText>
  )
}

/** The server requests consent; the footer explains what the next tap accepts. */
export function FiatTermsNotice({ terms }: FiatTermsNoticeProps) {
  const theme = useTheme()
  const isCoinbase = /^https:\/\/(www\.)?coinbase\.com\//.test(
    terms.documentUrl,
  )

  return (
    <Text
      weight="base"
      style={[
        styles.copy,
        { color: withAlpha(theme.content.elements.primary, 0.35) },
      ]}
    >
      {isCoinbase ? (
        <FunkitTrans
          i18nKey="fiat.coinbaseTermsNotice"
          components={{
            terms: <DocumentLink url={terms.documentUrl} />,
            agreement: <DocumentLink url={COINBASE_USER_AGREEMENT_URL} />,
            privacy: <DocumentLink url={COINBASE_PRIVACY_POLICY_URL} />,
          }}
        />
      ) : (
        <FunkitTrans
          i18nKey="fiat.termsNotice"
          components={{ terms: <DocumentLink url={terms.documentUrl} /> }}
        />
      )}
    </Text>
  )
}

// Native SF captions measure wider than Figma’s 10.5pt export; this preserves
// its two-line, 216pt measure without clipping the final document link.
const NOTICE_FONT_SIZE = 10.2

const styles = StyleSheet.create({
  link: {
    fontFamily: 'System',
    fontWeight: '600',
    fontSize: NOTICE_FONT_SIZE,
    lineHeight: 14,
    letterSpacing: -0.1575,
  },
  copy: {
    alignSelf: 'center',
    width: 216,
    maxWidth: '100%',
    fontFamily: 'System',
    fontWeight: '400',
    textAlign: 'center',
    fontSize: NOTICE_FONT_SIZE,
    lineHeight: 14,
    letterSpacing: -0.1575,
  },
})
